import { createFileRoute } from "@tanstack/react-router";
import { ctrDesc, ctrTitle } from "@/lib/seo-copy";
import { absoluteUrl } from "@/config/sites";
import { SITE } from "@/lib/site-config";
import { buildJsonLd, breadcrumbListSchema, webPageSchema, jsonLdScript } from "@/lib/schema";

const TITLE = `Editorial Standards — ${SITE.name}`;
const DESC = `How we research, review, and correct content on ${SITE.name}. Our commitment to accuracy, transparency, and expertise.`;

export const Route = createFileRoute("/editorial-standards")({
  loader: () => ({ h: (() => ({
    meta: [
      { title: ctrTitle(TITLE) },
      { name: "description", content: ctrDesc(DESC) },
      { property: "og:title", content: ctrTitle(TITLE) },
      { property: "og:description", content: ctrDesc(DESC) },
      { property: "og:url", content: absoluteUrl("/editorial-standards") },
      { property: "og:type", content: "article" },
    ],
    links: [{ rel: "canonical", href: absoluteUrl("/editorial-standards") }],
    scripts: [
      jsonLdScript(
        buildJsonLd([
          webPageSchema({ url: "/editorial-standards", title: TITLE, description: DESC }),
          breadcrumbListSchema([
            { name: "Home", url: "/" },
            { name: "Editorial Standards", url: "/editorial-standards" },
          ]),
        ]),
      ),
    ],
  }))() }),
  head: ({ loaderData }) => loaderData?.h ?? {},
  component: EditorialPage,
});

function EditorialPage() {
  return (
    <article className="mx-auto max-w-3xl px-4 py-16 prose prose-sm dark:prose-invert">
      <p className="text-xs uppercase tracking-widest text-muted-foreground">
        Trust & E-E-A-T
      </p>
      <h1>Editorial Standards</h1>

      <p data-speakable>
        Every article on {SITE.name} is written by a named expert, reviewed by
        a second qualified professional, and updated when the underlying facts
        change. We show authorship, credentials, and revision dates on every
        page.
      </p>

      <h2>1. Expertise</h2>
      <p>
        Our authors have direct professional experience in the topics they
        cover. Author bios include their credentials, professional bodies, and
        links to independent profiles.
      </p>

      <h2>2. Sourcing</h2>
      <p>
        Claims are backed by primary sources: statute text, regulator guidance,
        official case reports, or the original research being described. When
        we cite secondary reporting we link to it.
      </p>

      <h2>3. Review</h2>
      <p>
        A second qualified reviewer signs off on every published article and
        every substantive revision.
      </p>

      <h2>4. Corrections policy</h2>
      <p>
        If we get something wrong we correct it in place and note the change on
        the page. Report inaccuracies to{" "}
        <a href={`mailto:${SITE.contact.email}`}>{SITE.contact.email}</a>.
      </p>

      <h2>5. Independence</h2>
      <p>
        We do not accept payment for editorial coverage. Sponsored content,
        when it exists, is clearly labelled.
      </p>

      <h2>6. AI use</h2>
      <p>
        AI tools may assist with formatting or first-draft outlines, but every
        published sentence is verified and, where appropriate, rewritten by a
        human expert.
      </p>
    </article>
  );
}
